import { useApolloClient } from '@apollo/client';
import useStableCallback from 'src/hooks/useCallbackUtils/useStableCallback';
import { DEFAULT_LIST_PAGE_SIZE, hasListPageAfter, listPageCount, parseLimit, parseOffset, } from 'src/utils/pagination/listPagination';
import usePaginationState from 'src/utils/pagination/usePaginationState';
import { isSeoCrawler } from 'src/utils/seo/isSeoCrawler';
import { IPaginatedListTotalCountType } from '../../types';
import { isSelfComputeListLength } from '../../utils/selfCompute';
import useGQLQuery from '../useGQLQuery';
export default function useGQLPaginatedQuery(query, field, options = {}) {
    const mode = options.paginationMode ?? (isSeoCrawler ? 'navigation' : 'infinite');
    const isNavigation = mode === 'navigation';
    const pageSize = options.pageSize ?? DEFAULT_LIST_PAGE_SIZE;
    const { offset, limit, pageNo, hasPrev, buildPageTo, buildPageUrl } = usePaginationState({
        enabled: isNavigation,
        defaultLimit: pageSize,
    });
    const client = useApolloClient();
    const [queryState, queryActions] = useGQLQuery(query, {
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-and-network',
        ...options,
        lazy: true,
    });
    const data = queryState.data
        ? (() => {
            const raw = queryState.data[field];
            if (!raw) {
                return undefined;
            }
            const hasMore = (() => {
                const isSelfCompute = isSelfComputeListLength(raw);
                if (isSelfCompute) {
                    return raw.totalCount !== IPaginatedListTotalCountType.HAS_ENDED;
                }
                if (isNavigation) {
                    return hasListPageAfter(offset, raw.totalCount, limit);
                }
                return raw.list.length < raw.totalCount;
            })();
            return {
                ...raw,
                hasMore,
            };
        })()
        : undefined;
    const pagination = {
        mode,
        offset,
        limit,
        pageNo,
        totalPages: listPageCount(data?.totalCount ?? 0, limit),
        hasPrev,
        hasNext: data?.hasMore ?? false,
        buildPageTo,
        buildPageUrl,
    };
    const state = {
        ...queryState,
        data,
        pagination,
    };
    const actions = {
        refresh: useStableCallback((variables) => {
            options.listAnalytics?.custom('refresh');
            const effectiveOffset = isNavigation ? parseOffset(offset) : 0;
            const effectiveLimit = parseLimit(limit, pageSize);
            const fetchVariables = {
                ...variables,
                pagination: {
                    ...variables.pagination,
                    offset: effectiveOffset,
                    limit: effectiveLimit,
                },
            };
            if (isNavigation) {
                client.cache.evict({
                    id: 'ROOT_QUERY',
                    fieldName: field,
                });
                client.cache.gc();
                return queryActions.fetch(fetchVariables, {
                    fetchPolicy: 'network-only',
                });
            }
            return queryActions.fetch(fetchVariables);
        }),
        fetchMore: useStableCallback((variables) => {
            if (isNavigation)
                return;
            if (state.data &&
                state.data.hasMore &&
                !state.fetchingMore) {
                options.listAnalytics?.custom('next_page');
                return queryActions.fetchMore({
                    ...variables,
                    pagination: {
                        ...variables.pagination,
                        offset: state.data.list.length,
                        limit: parseLimit(limit, pageSize),
                    },
                });
            }
        }),
    };
    return [state, actions];
}
